import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import ReactPlayer from "react-player";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faVolumeLow,
  faVolumeHigh,
  faVolumeXmark,
  faHeart,
  faHeartCrack,
  faXmark,
  faUpRightFromSquare,
  faBackward,
  faForward,
  faChevronUp,
  faChevronDown,
  faClockRotateLeft,
  faUpRightAndDownLeftFromCenter,
  faDownLeftAndUpRightToCenter,
} from "@fortawesome/free-solid-svg-icons";

// Format seconds to mm:ss
const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Auto-scrolls (marquees) its text when it overflows the container.
// Renders a duplicate copy after the first so the loop is seamless for any length.
const ScrollingText = ({ as: Tag = "span", className = "", children }) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const text = textRef.current;
    if (!container || !text) return undefined;
    const measure = () => {
      setOverflows(text.scrollWidth > container.clientWidth + 1);
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) {
      ro.observe(container);
      ro.observe(text);
    }
    return () => { if (ro) ro.disconnect(); };
  }, [children]);

  return (
    <Tag ref={containerRef} className={`${className} scrolling-text${overflows ? " overflowing" : ""}`}>
      <span ref={textRef} className="scrolling-text-inner">{children}</span>
      {overflows && (
        <span className="scrolling-text-inner" aria-hidden="true">{children}</span>
      )}
    </Tag>
  );
};
import { resolveTenant } from "@whitelabel/utils";
import { useAuthService } from "@whitelabel/auth";
import { usePlayTracking } from "@whitelabel/hooks";
import { savePlaybackState, clearPlaybackState, loadPlaybackState } from "./playbackStorage.js";
import { useRecentlyPlayed } from "./useRecentlyPlayed.js";
import RecentlyPlayed, { RecentlyPlayedList } from "./RecentlyPlayed.jsx";
import SponsorBanner from "./SponsorBanner.jsx";
import useRotatingFallbackCover from "./useRotatingFallbackCover.js";
import {
  initialDriftState,
  observe as observeDrift,
  shouldResync,
  afterResync,
} from "./liveDrift.js";
import "./WebPlayer.css";

// Get or create a persistent anonymous session UUID for preference tracking
function getSessionUUID() {
  const key = 'rc_session_uuid';
  let uuid = localStorage.getItem(key);
  if (!uuid) {
    uuid = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(key, uuid);
  }
  return uuid;
}

const WebPlayer = ({
  session,
  config = {},
  streamUrl,
  metadataUrl,
  metadataOverride,
  onMinimize,
  onRestore,
  onPopout,
  showMinimizeButton = true,
  showPopoutButton = true,
  defaultDisplayMode = 'full',
  className = ""
}) => {
  const defaultStreamUrl = config.STREAM_URL;
  const isPlayingOverride = streamUrl && streamUrl !== defaultStreamUrl;
  
  console.log('[WebPlayer] Stream check:', { 
    streamUrl, 
    defaultStreamUrl, 
    isPlayingOverride 
  });
  // User intent (what the user asked for) — drives ReactPlayer.playing prop + save state
  const [intentToPlay, setIntentToPlay] = useState(false);
  // Actual audio element state — driven ONLY by ReactPlayer callbacks, never by clicks
  const [isPlaying, setIsPlaying] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  // Use Auth0 service for authentication
  const { isAuthenticated, isLoading, user, login, logout, getToken } = useAuthService();
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const prevVolumeRef = useRef(0.8);
  const defaultTitle = config.META_TITLE_OVERRIDE || "Streaming";
  const defaultArtist = config.META_ARTIST_OVERRIDE || "Radio";
  // First fallback-pool image (FB4-6), else the station logo. Also used as
  // the recently-played thumbnail fallback (static — no rotation in the list).
  const defaultCover =
    config.FALLBACK_IMAGES?.items?.[0]?.src || config.LOGO_URL || "";
  const adMessage = config.AD_MESSAGE_TEMPLATE
    ? config.AD_MESSAGE_TEMPLATE.replace('{tenant}', config.STATION_NAME || 'Radio')
    : `Thank you for listening to ${config.STATION_NAME || 'Radio'}`;
  // Ad-break markers vary by metadata source — the encoder sends ADWTAG_30000 /
  // ADWTAG_60000, the CDN sends "Commercial Ad Break". Per-tenant so a station's
  // own wording never needs a code change. Kept as a joined string so it stays a
  // stable dependency for the polling effect below.
  const adMarkersKey = (
    Array.isArray(config.AD_MARKERS) && config.AD_MARKERS.length
      ? config.AD_MARKERS
      : ["adwtag"]
  )
    .map((m) => String(m).toLowerCase())
    .join("|");
  const [metadata, setMetadata] = useState({
    title: defaultTitle,
    artist: defaultArtist,
    cover: defaultCover,
    duration: 0,
    currentTime: 0,
    trackId: null,
  });

  // What actually renders as album art: real covers pass through untouched;
  // fallback covers rotate through the tenant's configured pool (FB4-6).
  const displayCover = useRotatingFallbackCover(
    metadata.cover,
    config.FALLBACK_IMAGES,
    config.LOGO_URL,
  );

  // Display mode: 'full' | 'bar' | 'minimized'
  const [displayMode, setDisplayMode] = useState(defaultDisplayMode);
  const playButtonRef = useRef();
  const prevTrackIdRef = useRef(null);
  const playerRef = useRef(null);

  // How far live playback has fallen behind wall clock (RCS-33). Kept in a ref
  // rather than state: it updates twice a second and nothing renders from it.
  const driftRef = useRef(initialDriftState());

  // Seeking state (to prevent progress updates while user is dragging)
  const [isSeeking, setIsSeeking] = useState(false);

  // Auto-resume playback from saved state (e.g., after page navigation)
  useEffect(() => {
    const savedState = loadPlaybackState();
    if (!savedState || !savedState.playing) return;

    console.log('[WebPlayer] Resuming playback from saved state:', savedState.title);

    // Restore metadata immediately so the UI doesn't flash defaults
    setMetadata((prev) => ({
      ...prev,
      title: savedState.title || prev.title,
      artist: savedState.artist || prev.artist,
      cover: savedState.cover || prev.cover,
      duration: savedState.duration || prev.duration,
    }));

    // Restore display mode
    if (savedState.displayMode) {
      setDisplayMode(savedState.displayMode);
    }

    // Restore intent — ReactPlayer will try to match it. If the browser
    // blocks autoplay, isPlaying stays false and the button correctly
    // shows "play" until the user clicks.
    setIntentToPlay(true);

    // For on-demand content, seek to saved position after a short delay
    if (!savedState.isLive && savedState.position > 0) {
      const seekTimer = setTimeout(() => {
        if (playerRef.current) {
          playerRef.current.seekTo(savedState.position, 'seconds');
          setMetadata((prev) => ({ ...prev, currentTime: savedState.position }));
        }
      }, 500);
      return () => clearTimeout(seekTimer);
    }

    // Clear saved state after resuming
    clearPlaybackState();
  }, []); // Only on mount

  const handleMinimize = () => {
    setDisplayMode('minimized');
    onMinimize?.();
  };

  const handleCollapse = () => {
    setDisplayMode('bar');
  };

  const handleExpand = () => {
    setDisplayMode('full');
    onRestore?.();
  };

  // Desktop-only: grow the bottom strip into the large two-column panel.
  const handleExpandedOpen = () => {
    setDisplayMode('expanded');
    onRestore?.();
  };

  // Collapse the expanded panel back to the bottom strip.
  const handleExpandedClose = () => {
    setDisplayMode('full');
  };

  // --- Vertical swipe detection (mobile thumb gesture) ---------------------
  // Single-responsibility helper: records the touch start point and, on
  // touch end, invokes the supplied callback only for a clear vertical swipe
  // in the requested direction. Reuses the existing transition functions
  // (handleExpand / handleCollapse) — it never changes display mode itself.
  // A real vertical drag must exceed SWIPE_MIN_DISTANCE and clearly dominate
  // the horizontal movement, so taps on bar buttons and horizontal drags
  // (e.g. the seek slider) are ignored and their own handlers still fire.
  const SWIPE_MIN_DISTANCE = 45; // px
  const swipeStartRef = useRef(null);

  const handleSwipeStart = (e) => {
    const touch = e.touches && e.touches[0];
    swipeStartRef.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
  };

  // direction: 'up' calls onSwipe for an upward drag, 'down' for a downward drag.
  const makeSwipeEndHandler = (direction, onSwipe) => (e) => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;
    if (!start) return;
    const touch = e.changedTouches && e.changedTouches[0];
    if (!touch) return;
    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    // Vertical only: must be a long enough drag that dominates horizontal motion.
    if (Math.abs(deltaY) < SWIPE_MIN_DISTANCE) return;
    if (Math.abs(deltaY) <= Math.abs(deltaX)) return;
    if (direction === 'up' && deltaY < 0) onSwipe();
    if (direction === 'down' && deltaY > 0) onSwipe();
  };

  // Restore from minimized — clicking the minimized strip opens bar mode
  const handleRestore = (e) => {
    if (playButtonRef.current && playButtonRef.current.contains(e.target))
      return;
    if (displayMode === 'minimized') {
      setDisplayMode('bar');
      onRestore?.();
    }
  };

  const popoutPlayer = () => {
    const popup = window.open(
      "/popout-player",
      "_blank",
      "width=240,height=410,toolbar=0,location=0,status=0,menubar=0,resizable=no,scrollbars=no"
    );
    if (popup) popup.focus();
    setDisplayMode('minimized');
    onPopout?.();
  };

  const [like, setLike] = useState(false);
  const [dislike, setDislike] = useState(false);

  const tenantId = resolveTenant();

  // Recently-played popup. Derive the endpoint from metadataUrl (same base +
  // tenant slug across all surfaces); fall back to the relative content path.
  const [showRecentlyPlayed, setShowRecentlyPlayed] = useState(false);
  const recentlyPlayedUrl = metadataUrl
    ? metadataUrl.replace(/\/metadata(\?.*)?$/, "/recently-played")
    : `/api/contentmanagement/tenants/${tenantId}/recently-played`;
  // Fetch while the popup is open OR the expanded panel (which shows the list
  // inline) is active; refetch when the live track changes.
  const recentlyPlayed = useRecentlyPlayed(recentlyPlayedUrl, {
    enabled: showRecentlyPlayed || displayMode === 'expanded',
    refreshKey: metadata.trackId,
  });

  // Track play events (driven by actual audio state, not intent)
  usePlayTracking(metadata.trackId, isPlaying, session);

  const togglePlay = () => {
    setIntentToPlay((prev) => {
      if (prev) {
        // User is pausing — clear saved state so we don't auto-resume
        clearPlaybackState();
      }
      return !prev;
    });
  };

  // Expanded mode is desktop/landscape only. If the viewport is (or becomes)
  // portrait — where `full` is already the big view — drop out of expanded.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(orientation: portrait)');
    const handle = () => {
      if (mq.matches) setDisplayMode((m) => (m === 'expanded' ? 'full' : m));
    };
    handle();
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, []);

  // Listen for forcePlay event (when a card is clicked)
  useEffect(() => {
    const handleForcePlay = () => {
      console.log('[WebPlayer] Force play triggered');
      setIntentToPlay(true);
    };

    window.addEventListener('forcePlay', handleForcePlay);
    return () => window.removeEventListener('forcePlay', handleForcePlay);
  }, []);

  // Listen for seekTo event (for resume functionality)
  useEffect(() => {
    const handleSeekTo = (e) => {
      const { position } = e.detail;
      if (playerRef.current && position !== undefined) {
        console.log('[WebPlayer] Seeking to position:', position);
        playerRef.current.seekTo(position, 'seconds');
        setMetadata((prev) => ({ ...prev, currentTime: position }));
      }
    };

    window.addEventListener('seekTo', handleSeekTo);
    return () => window.removeEventListener('seekTo', handleSeekTo);
  }, []);

  const getCookie = (name) => {
    const match = document.cookie.match(
      new RegExp("(^| )" + name + "=([^;]+)")
    );
    return match ? match[2] : null;
  };

  const title = metadata?.title || "";

  /**
   * Bring live playback back to the live edge after it has fallen behind.
   *
   * There is nothing to seek to. This is an endless ICY stream, so the audio
   * element exposes no useful seekable range -- the only way back is to drop
   * the connection and take a new one, which costs the listener a moment of
   * audio and returns them to the normal ~31s-behind-live position.
   */
  const resyncToLive = () => {
    const audio = playerRef.current?.getInternalPlayer?.();
    if (!audio || typeof audio.load !== "function") return;

    driftRef.current = afterResync();
    try {
      audio.load();
      // load() drops the playing state, so intent has to be reasserted.
      const resumed = audio.play?.();
      if (resumed?.catch) resumed.catch(() => {});
    } catch {
      // A failed reconnect is not worth breaking playback over. The drift
      // state is already cleared, so the next threshold crossing tries again.
    }
  };

  // ReactPlayer progress callback - updates currentTime from actual playback
  const handleProgress = (state) => {
    // Only update if not seeking (user dragging the slider)
    if (!isSeeking && isPlayingOverride) {
      setMetadata((prev) => ({
        ...prev,
        currentTime: state.playedSeconds,
      }));
    }

    // Live only. On-demand tracks are finite files that the listener may
    // legitimately pause, scrub or leave sitting -- reconnecting one would
    // restart it from the beginning.
    if (isPlayingOverride) return;

    driftRef.current = observeDrift(driftRef.current, {
      playing: intentToPlay,
      wallMs: Date.now(),
      positionSeconds: state.playedSeconds,
    });

    if (shouldResync(driftRef.current)) {
      console.log(
        `[WebPlayer] live playback ${driftRef.current.driftSeconds.toFixed(1)}s behind, reconnecting`,
      );
      resyncToLive();
    }
  };

  // ReactPlayer duration callback - gets actual duration of audio file
  const handleDuration = (duration) => {
    if (isPlayingOverride) {
      setMetadata((prev) => ({
        ...prev,
        duration: duration,
      }));
    }
  };

  // Seek handlers for progress bar
  const handleSeekMouseDown = () => {
    setIsSeeking(true);
  };

  const handleSeekChange = (e) => {
    const newTime = parseFloat(e.target.value);
    setMetadata((prev) => ({
      ...prev,
      currentTime: newTime,
    }));
  };

  const handleSeekMouseUp = (e) => {
    setIsSeeking(false);
    const newTime = parseFloat(e.target.value);
    if (playerRef.current) {
      playerRef.current.seekTo(newTime, 'seconds');
    }
  };

  // Skip forward/backward (for on-demand content)
  const skipForward = (seconds = 10) => {
    if (playerRef.current && isPlayingOverride) {
      const newTime = Math.min(metadata.currentTime + seconds, metadata.duration);
      playerRef.current.seekTo(newTime, 'seconds');
      setMetadata((prev) => ({ ...prev, currentTime: newTime }));
    }
  };

  const skipBackward = (seconds = 10) => {
    if (playerRef.current && isPlayingOverride) {
      const newTime = Math.max(metadata.currentTime - seconds, 0);
      playerRef.current.seekTo(newTime, 'seconds');
      setMetadata((prev) => ({ ...prev, currentTime: newTime }));
    }
  };

  // Apply metadata override when provided
  useEffect(() => {
    if (metadataOverride) {
      console.log('[WebPlayer] Applying metadata override:', metadataOverride);
      setMetadata((prev) => ({
        ...prev,
        title: metadataOverride.title,
        artist: metadataOverride.artist,
        cover: metadataOverride.cover,
        trackId: null, // Clear trackId for override content
      }));
    }
  }, [metadataOverride]);

  // Fetch metadata every 10s (skip if we have an override)
  useEffect(() => {
    if (!metadataUrl || metadataOverride) return;

    const fetchMetadata = async () => {
      try {
        const { data } = await axios.get(metadataUrl, {
          headers: { "X-Tenant-ID": tenantId },
        });
        // Markers arrive uppercase from the encoder (ADWTAG_30000/ADWTAG_60000),
        // so match case-insensitively or the raw tag renders as a track title.
        const lowerTitle = data.title?.toLowerCase() ?? "";
        if (adMarkersKey.split("|").some((m) => lowerTitle.startsWith(m))) {
          setMetadata((prev) => ({
            ...prev,
            trackId: data.track_id ?? null,
            title: adMessage,
            artist: "",
            cover: defaultCover,
            duration: prev.duration,
            currentTime: isPlaying ? prev.currentTime : 0,
          }));
        } else {
          setMetadata((prev) => ({
            ...prev,
            trackId: data.track_id ?? null,
            title: data.title,
            artist: data.artist,
            cover: data.cover,
            duration: data.duration || prev.duration,
            currentTime: isPlaying ? prev.currentTime : 0,
          }));
        }
      } catch (error) {
        console.error("Error fetching metadata:", error);
      }
    };

    const fetchInitialData = async () => {
      await fetchMetadata();
    };

    fetchInitialData();

    const interval = setInterval(fetchMetadata, 10000);
    return () => clearInterval(interval);
  }, [isPlaying, metadataUrl, tenantId, defaultTitle, defaultArtist, defaultCover, adMessage, adMarkersKey, metadataOverride]);

  // Fetch user preference when track changes or user logs in
  useEffect(() => {
    const fetchPreference = async () => {
      // Skip if trackId hasn't actually changed
      if (metadata.trackId === prevTrackIdRef.current) {
        return;
      }

      // Reset preferences if no trackId
      if (!metadata.trackId) {
        setLike(false);
        setDislike(false);
        prevTrackIdRef.current = metadata.trackId;
        return;
      }

      // Validate trackId is a valid number
      const trackIdInt = parseInt(metadata.trackId);
      if (isNaN(trackIdInt) || trackIdInt <= 0) {
        console.log("Invalid track ID, skipping preference fetch:", metadata.trackId);
        setLike(false);
        setDislike(false);
        prevTrackIdRef.current = metadata.trackId;
        return;
      }

      try {
        const headers = await getPreferenceHeaders();

        const response = await fetch(`/api/usermanagement/preferences/tracks/${trackIdInt}`, {
          method: "GET",
          headers,
        });

        if (response.ok) {
          const data = await response.json();
          // Update UI based on fetched preference
          if (data.preference === 'like') {
            setLike(true);
            setDislike(false);
          } else if (data.preference === 'dislike') {
            setLike(false);
            setDislike(true);
          } else {
            setLike(false);
            setDislike(false);
          }
        }

        // Update ref after successful or failed fetch
        prevTrackIdRef.current = metadata.trackId;
      } catch (error) {
        console.error("Error fetching track preference:", error);
        prevTrackIdRef.current = metadata.trackId;
      }
    };

    fetchPreference();
  }, [metadata.trackId, isAuthenticated, tenantId]);

  // Save playback state on visibility change (phone call, tab switch, etc.)
  useEffect(() => {
    const saveCurrentState = () => {
      // Persist what the user asked for, not a transient actual-playing flicker
      if (intentToPlay && streamUrl) {
        savePlaybackState({
          url: streamUrl,
          position: metadata.currentTime,
          title: metadata.title,
          artist: metadata.artist,
          cover: metadata.cover,
          duration: metadata.duration,
          isLive: !isPlayingOverride,
          playing: true,
          displayMode,
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        console.log('[WebPlayer] Page hidden, saving playback state');
        saveCurrentState();
      }
    };

    const handleBeforeUnload = () => {
      console.log('[WebPlayer] Page unloading, saving playback state');
      saveCurrentState();
    };

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('blur', saveCurrentState);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('blur', saveCurrentState);
    };
  }, [intentToPlay, streamUrl, metadata, isPlayingOverride, displayMode]);

  // Clear saved state when playback completes
  useEffect(() => {
    if (!isPlaying && metadata.currentTime > 0 && metadata.duration > 0) {
      // If we're near the end, clear the state (playback completed)
      if (metadata.currentTime >= metadata.duration - 1) {
        console.log('[WebPlayer] Playback completed, clearing saved state');
        clearPlaybackState();
      }
    }
  }, [isPlaying, metadata.currentTime, metadata.duration]);

  // Build preference request headers (auth token or anonymous session)
  const getPreferenceHeaders = async (includeContentType = false) => {
    const headers = { "X-Tenant-ID": tenantId };
    if (includeContentType) headers["Content-Type"] = "application/json";

    if (isAuthenticated) {
      const token = await getToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
    } else {
      headers["X-Session-ID"] = getSessionUUID();
    }
    return headers;
  };

  // Persist a like/dislike for any track. preference: "like" | "dislike" | null
  // (null clears it). Shared by the live player hearts and the recently-played
  // list rows so the API call lives in one place.
  const setTrackPreference = async (trackId, preference) => {
    if (!trackId) return;
    try {
      if (preference === null) {
        await fetch(`/api/usermanagement/preferences/tracks/${trackId}`, {
          method: "DELETE",
          headers: await getPreferenceHeaders(),
        });
      } else {
        await fetch(`/api/usermanagement/preferences/tracks/${trackId}`, {
          method: "POST",
          headers: await getPreferenceHeaders(true),
          body: JSON.stringify({ preference }),
        });
      }
    } catch (error) {
      console.error("Error updating track preference:", error);
    }
  };

  //Like handle
  const handleLike = async () => {
    const newLike = !like;
    setLike(newLike);
    setDislike(false);
    await setTrackPreference(metadata.trackId, newLike ? "like" : null);
  };

  //Dislike handle
  const handleDislike = async () => {
    const newDislike = !dislike;
    setDislike(newDislike);
    setLike(false);
    await setTrackPreference(metadata.trackId, newDislike ? "dislike" : null);
  };

  const handleAuthLogin = async () => {
    try {
      await login(true); // preserveSession = true for anonymous session migration
      setShowLogin(false);
    } catch (err) {
      console.error("Auth login failed:", err);
    }
  };

  // Auth logout handler
  const handleAuthLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error("Auth logout failed:", err);
    }
  };

  return (
    <div
      className={`player-container ${displayMode} ${isPlayingOverride ? "on-demand" : ""} ${className}`}
      onClick={displayMode === 'minimized' ? handleRestore : undefined}
    >
      {/* Hidden Audio Player — intent drives playing prop, callbacks drive isPlaying */}
      <ReactPlayer
        ref={playerRef}
        className="react-player"
        url={streamUrl}
        playing={intentToPlay}
        volume={muted ? 0 : volume}
        controls={false}
        width="0"
        height="0"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => { setIsPlaying(false); setIntentToPlay(false); }}
        onError={() => { setIsPlaying(false); setIntentToPlay(false); }}
        onProgress={handleProgress}
        onDuration={handleDuration}
        progressInterval={500}
        config={{ file: { attributes: { preload: "auto", buffer: 3 } } }}
      />

      {/* === BAR LAYOUT — compact bar, shown only in portrait bar mode via CSS === */}
      <div
        className="player-bar"
        onTouchStart={handleSwipeStart}
        onTouchEnd={makeSwipeEndHandler('up', handleExpand)}
      >
        <img className="bar-album-art" src={displayCover} alt="Album Art" />
        <div className="bar-body">
          {/* Top row: title/artist */}
          <div className="bar-top">
            <div className="bar-metadata">
              <ScrollingText className="bar-title">{title}</ScrollingText>
              <span className="bar-artist">{metadata.artist}</span>
            </div>
          </div>
          {/* Bottom row: transport + likes */}
          <div className="bar-bottom">
            <div className="bar-controls" ref={displayMode === 'bar' ? playButtonRef : undefined}>
              <button
                aria-label={isPlaying ? "Pause" : "Play"}
                onClick={togglePlay}
                className={`play-button bar-play ${isPlaying ? "playing" : ""}`}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  {isPlaying ? (
                    <path d="M6 4h4v16h-4zM14 4h4v16h-4z" />
                  ) : (
                    <path d="M8 5v14l11-7z" />
                  )}
                </svg>
              </button>
              {!isPlayingOverride && (
                <div className="live-badge bar-live">LIVE</div>
              )}
            </div>
            <div className="bar-likes">
              <button
                className={`heart ${like ? "active" : ""}`}
                onClick={handleLike}
                aria-label="Like"
              >
                <FontAwesomeIcon icon={faHeart} />
              </button>
              <button
                className={`heartcrack ${dislike ? "active" : ""}`}
                onClick={handleDislike}
                aria-label="Dislike"
              >
                <FontAwesomeIcon icon={faHeartCrack} />
              </button>
            </div>
          </div>
        </div>
        {/* Right edge: utility buttons in their own slim column, separated
            from the title/transport rows */}
        <div className="bar-actions">
          {showMinimizeButton && (
            <button
              className="bar-close-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleMinimize();
              }}
              aria-label="Close Player"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
          <button
            className="recently-played-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowRecentlyPlayed(true);
            }}
            aria-label="Recently Played"
          >
            <FontAwesomeIcon icon={faClockRotateLeft} />
          </button>
          <button
            className="bar-expand-btn"
            onClick={handleExpand}
            aria-label="Expand Player"
          >
            <FontAwesomeIcon icon={faChevronUp} />
          </button>
        </div>
      </div>

      {/* === FULL LAYOUT — always rendered, CSS controls visibility === */}
      <div className="player-full-content">
        {/* Mobile-only drag handle: swipe down collapses full → bar.
            Scoped to this top strip (not the whole panel) so a content
            scroll in the tall portrait column isn't misread as a collapse. */}
        <div
          className="player-drag-handle"
          onTouchStart={handleSwipeStart}
          onTouchEnd={makeSwipeEndHandler('down', handleCollapse)}
          aria-hidden="true"
        />
        <div className="player-main">
          {/* === LEFT SECTION: Album Art + Metadata === */}
          <div className="player-left">
            <div className="album-art">
              <img src={displayCover} alt="Album Art" />
            </div>
            <div className="like-buttons">
              <button
                className={`heart ${like ? "active" : ""}`}
                onClick={handleLike}
                aria-label="Like"
              >
                <FontAwesomeIcon icon={faHeart} />
              </button>
              <button
                className={`heartcrack ${dislike ? "active" : ""}`}
                onClick={handleDislike}
                aria-label="Dislike"
              >
                <FontAwesomeIcon icon={faHeartCrack} />
              </button>
            </div>
            <div className="metadata">
              <ScrollingText as="p" className="title">{title}</ScrollingText>
              <p className="artist">{metadata.artist}</p>
              {isPlayingOverride && (
                <button
                  className="back-to-live-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('[WebPlayer] Back to Live clicked');
                    window.dispatchEvent(new CustomEvent('backToLive'));
                  }}
                  aria-label="Back to Live Stream"
                >
                  ← Back to Live
                </button>
              )}
            </div>
          </div>

          {/* === CENTER SECTION: Controls + Seek === */}
          <div className="player-center" ref={displayMode !== 'bar' ? playButtonRef : undefined}>
            <div className="controls-row">
              {isPlayingOverride && (
                <button
                  aria-label="Skip backward 10 seconds"
                  onClick={() => skipBackward(10)}
                  className="skip-button"
                >
                  <FontAwesomeIcon icon={faBackward} />
                </button>
              )}

              <button
                aria-label={isPlaying ? "Pause" : "Play"}
                onClick={togglePlay}
                className={`play-button ${isPlaying ? "playing" : ""}`}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  {isPlaying ? (
                    <path d="M6 4h4v16h-4zM14 4h4v16h-4z" />
                  ) : (
                    <path d="M8 5v14l11-7z" />
                  )}
                </svg>
              </button>

              {isPlayingOverride && (
                <button
                  aria-label="Skip forward 10 seconds"
                  onClick={() => skipForward(10)}
                  className="skip-button"
                >
                  <FontAwesomeIcon icon={faForward} />
                </button>
              )}
            </div>

            {isPlayingOverride && (
              <div className="seek-row">
                <span className="time-display">{formatTime(metadata.currentTime)}</span>
                <input
                  type="range"
                  min="0"
                  max={metadata.duration || 100}
                  value={metadata.currentTime}
                  onChange={handleSeekChange}
                  onMouseDown={handleSeekMouseDown}
                  onMouseUp={handleSeekMouseUp}
                  onTouchStart={handleSeekMouseDown}
                  onTouchEnd={handleSeekMouseUp}
                  className="seek-bar"
                  style={{
                    background: `linear-gradient(to right, var(--player-accent, #dd3333) ${(metadata.currentTime / (metadata.duration || 1)) * 100}%, rgba(255,255,255,0.3) 0%)`
                  }}
                />
                <span className="time-display">{formatTime(metadata.duration)}</span>
              </div>
            )}

            {!isPlayingOverride && (
              <div className="live-badge">LIVE</div>
            )}
          </div>

          {/* === RIGHT SECTION: Volume === */}
          <div className="player-right">
            <div className="volume-control">
              <button
                className="mute-btn"
                onClick={() => {
                  if (muted) {
                    setMuted(false);
                    setVolume(prevVolumeRef.current || 0.8);
                  } else {
                    prevVolumeRef.current = volume;
                    setMuted(true);
                  }
                }}
                aria-label={muted ? "Unmute" : "Mute"}
              >
                <FontAwesomeIcon icon={muted ? faVolumeXmark : volume > 0.5 ? faVolumeHigh : faVolumeLow} />
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={muted ? 0 : volume}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  setVolume(v);
                  if (v > 0) setMuted(false);
                }}
              />
            </div>
          </div>
        </div>

        {/* Player Control Buttons */}
        <div className="player-buttons">
          <button
            className="recently-played-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowRecentlyPlayed(true);
            }}
            aria-label="Recently Played"
          >
            <FontAwesomeIcon icon={faClockRotateLeft} />
          </button>
          {/* Desktop-only: grow into the two-column expanded panel */}
          <button
            className="expand-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleExpandedOpen();
            }}
            aria-label="Expand Player"
          >
            <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} />
          </button>
          {showMinimizeButton && (
            <button
              className="collapse-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleCollapse();
              }}
              aria-label="Collapse to bar"
            >
              <FontAwesomeIcon icon={faChevronDown} />
            </button>
          )}
          {showMinimizeButton && (
            <button
              className="minimize-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleMinimize();
              }}
              aria-label="Minimize Player"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
          {showPopoutButton && window.innerWidth >= 1024 && (
            <button
              className="popout-btn"
              onClick={popoutPlayer}
              aria-label="Popout Player"
            >
              <FontAwesomeIcon icon={faUpRightFromSquare} />
            </button>
          )}
        </div>

        {/* === Unified Footer: Socials + Login === */}
        <div className="footer-bar-wrapper">
          <div
            className={`social-buttons-container ${showLogin ? "dithered" : ""}`}
          >
            {Object.entries(config.SOCIAL || {}).map(
              ([platform, url]) =>
                url && (
                  <a
                    key={platform}
                    href={url}
                    className="btIconWidget"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="btIconWidgetIcon">
                      <i className={`fab fa-${platform.toLowerCase()}`}></i>
                    </div>
                  </a>
                )
            )}
          </div>
          <div className="login-wrapper">
            <div
              className="login-avatar"
              onClick={() => setShowLogin(!showLogin)}
            >
              {isAuthenticated && user ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  style={{width: '32px', height: '32px', borderRadius: '50%'}}
                />
              ) : (
                <i className="fas fa-user-circle"></i>
              )}
            </div>

            {!isAuthenticated && showLogin && (
              <div className="login-form-inline">
                <button
                  type="button"
                  onClick={handleAuthLogin}
                  style={{marginRight: '8px'}}
                >
                  Sign In
                </button>
                <button
                  type="button"
                  onClick={() => setShowLogin(false)}
                  style={{background: '#666'}}
                >
                  Continue as Guest
                </button>
              </div>
            )}

            {isAuthenticated && showLogin && (
              <div className="login-form-inline">
                <span style={{color: 'white', marginRight: '8px'}}>
                  {user?.name || user?.email}
                </span>
                <button
                  type="button"
                  onClick={handleAuthLogout}
                  style={{background: '#666'}}
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* === MINIMIZED MODE — tiny strip with play button === */}
      <div className="minimized-strip">
        <button
          ref={displayMode === 'minimized' ? playButtonRef : undefined}
          aria-label={isPlaying ? "Pause" : "Play"}
          onClick={(e) => {
            e.stopPropagation();
            togglePlay();
          }}
          className={`play-button minimized-play ${isPlaying ? "playing" : ""}`}
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            {isPlaying ? (
              <path d="M6 4h4v16h-4zM14 4h4v16h-4z" />
            ) : (
              <path d="M8 5v14l11-7z" />
            )}
          </svg>
        </button>
      </div>

      {/* === EXPANDED SIDE PANEL — desktop right column (recently played + ad) ===
          Rendered always; CSS shows it only in expanded mode. The now-playing
          left column reuses .player-full-content above. */}
      <div className="player-expanded-side">
        <div className="expanded-side-header">
          <span>Recently Played</span>
          <button
            className="expanded-collapse-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleExpandedClose();
            }}
            aria-label="Collapse Player"
          >
            <FontAwesomeIcon icon={faDownLeftAndUpRightToCenter} />
          </button>
        </div>
        <RecentlyPlayedList
          tracks={recentlyPlayed.tracks}
          loading={recentlyPlayed.loading}
          error={recentlyPlayed.error}
          currentTrackId={metadata.trackId}
          fallbackCover={defaultCover}
          onSetPreference={setTrackPreference}
        />
        <SponsorBanner
          items={config.SPONSORS?.items}
          interval={config.SPONSORS?.interval}
        />
      </div>

      {showRecentlyPlayed && (
        <RecentlyPlayed
          tracks={recentlyPlayed.tracks}
          loading={recentlyPlayed.loading}
          error={recentlyPlayed.error}
          currentTrackId={metadata.trackId}
          fallbackCover={defaultCover}
          onSetPreference={setTrackPreference}
          onClose={() => setShowRecentlyPlayed(false)}
        />
      )}
    </div>
  );
};

export default WebPlayer;